import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { localizePath, useCurrentLang } from "@/i18n/language";
import { parseISODate, toISODate } from "@/components/base/DatePicker";
import { getVillaAvailability, getVillaBlockedDates } from "@/lib/villaApi";
import AvailabilityCalendar from "./AvailabilityCalendar";
import CheckInOutNote from "@/components/feature/CheckInOutNote";

const WHOLE_PROPERTY_SLUG = "villa-gesamt";

const VILLAS = [
  { slug: "villa-frangipani", nameDe: "Villa Frangipani", nameEn: "Villa Frangipani" },
  { slug: "villa-bougainvillea", nameDe: "Villa Bougainvillea", nameEn: "Villa Bougainvillea" },
  { slug: "villa-hibiskus", nameDe: "Villa Hibiskus", nameEn: "Villa Hibiscus" },
  { slug: WHOLE_PROPERTY_SLUG, nameDe: "Gesamtes Anwesen (alle 3 Villen)", nameEn: "Entire property (all 3 villas)" },
];

const ALL_AVAILABILITY_SLUGS = [
  "villa-frangipani",
  "villa-bougainvillea",
  "villa-hibiskus",
  WHOLE_PROPERTY_SLUG,
];

// Slugs whose bookings/blocked dates make the selected option unavailable.
// A whole-property booking blocks every villa, and any single villa booking
// also blocks the whole property.
const BLOCKING_SOURCES: Record<string, string[]> = {
  "villa-frangipani": ["villa-frangipani", WHOLE_PROPERTY_SLUG],
  "villa-bougainvillea": ["villa-bougainvillea", WHOLE_PROPERTY_SLUG],
  "villa-hibiskus": ["villa-hibiskus", WHOLE_PROPERTY_SLUG],
  [WHOLE_PROPERTY_SLUG]: ["villa-frangipani", "villa-bougainvillea", "villa-hibiskus", WHOLE_PROPERTY_SLUG],
};

interface Reservation {
  check_in: string;
  check_out: string;
}

interface AvailabilityData {
  /** Booked/blocked nights (occupied). */
  occupied: Set<string>;
  /** Days on which an existing guest departs = arrival half-day. */
  checkOuts: Set<string>;
  /** Days on which a new guest arrives = departure half-day. */
  checkIns: Set<string>;
}

function buildAvailability(reservations: Reservation[], blocked: string[]): AvailabilityData {
  const occupied = new Set<string>(blocked);
  const checkOuts = new Set<string>();
  const checkIns = new Set<string>();
  for (const r of reservations) {
    const start = parseISODate(r.check_in);
    const end = parseISODate(r.check_out);
    if (!start || !end) continue;
    checkIns.add(r.check_in);
    checkOuts.add(r.check_out);
    const cur = new Date(start);
    while (cur < end) {
      occupied.add(toISODate(cur));
      cur.setDate(cur.getDate() + 1);
    }
  }
  return { occupied, checkOuts, checkIns };
}

interface AvailabilityModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AvailabilityModal({ open, onClose }: AvailabilityModalProps) {
  const navigate = useNavigate();
  const lang = useCurrentLang();
  const isDe = lang === "de";

  const today = new Date();
  const todayStr = toISODate(today);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  const maxDateStr = toISODate(maxDate);

  const [villaSlug, setVillaSlug] = useState(VILLAS[0].slug);
  const [rawAvailability, setRawAvailability] = useState<
    Record<string, { reservations: Reservation[]; blockedDates: string[] }>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [hover, setHover] = useState<string | null>(null);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const minDate = parseISODate(todayStr);
  const maxD = parseISODate(maxDateStr);

  // Load availability for every villa (and the whole property) whenever the
  // modal opens, so combined blocking across villas works while switching tabs.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all(
      ALL_AVAILABILITY_SLUGS.map(async (slug) => {
        const [resRes, blockRes] = await Promise.all([
          getVillaAvailability(slug),
          getVillaBlockedDates(slug),
        ]);
        const value =
          resRes.error || blockRes.error
            ? { reservations: [] as Reservation[], blockedDates: [] as string[] }
            : {
                reservations: (resRes.data ?? []) as Reservation[],
                blockedDates: ((blockRes.data ?? []) as { blocked_date: string }[]).map(
                  (d) => d.blocked_date
                ),
              };
        return [slug, value] as const;
      })
    )
      .then((entries) => {
        if (!active) return;
        setRawAvailability(Object.fromEntries(entries));
      })
      .catch(() => {
        if (active) {
          setError(true);
          setRawAvailability({});
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, reloadKey]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Merge availability across the blocking sources of the selected option.
  const { reservations, blocked } = useMemo(() => {
    const sources = BLOCKING_SOURCES[villaSlug] ?? [villaSlug];
    const reservations: Reservation[] = [];
    const blocked: string[] = [];
    for (const s of sources) {
      const a = rawAvailability[s];
      if (!a) continue;
      reservations.push(...a.reservations);
      blocked.push(...a.blockedDates);
    }
    return { reservations, blocked };
  }, [rawAvailability, villaSlug]);

  const availabilityData = useMemo(
    () => buildAvailability(reservations, blocked),
    [reservations, blocked]
  );

  const selectingStart = !checkIn || (!!checkIn && !!checkOut);

  // First occupied night after the chosen check-in = the next guest's arrival day.
  // That is exactly the departure half-day on which our guest may check out.
  const firstOccupiedNight = useMemo(() => {
    if (!checkIn) return null;
    const start = parseISODate(checkIn);
    if (!start) return null;
    let first: string | null = null;
    for (const iso of availabilityData.occupied) {
      const d = parseISODate(iso);
      if (d && d > start && (!first || iso < first)) first = iso;
    }
    return first;
  }, [checkIn, availabilityData]);

  // Half-day (turnover) handling, mirroring the booking form calendar:
  // picking arrival -> existing check-out days are arrival half-days;
  // picking departure -> the next arrival day is the departure half-day.
  const calTurnover = useMemo(() => {
    if (selectingStart) return availabilityData.checkOuts;
    return firstOccupiedNight ? new Set([firstOccupiedNight]) : new Set<string>();
  }, [selectingStart, availabilityData, firstOccupiedNight]);

  const calUnavailable = useMemo(() => {
    if (selectingStart) return availabilityData.occupied;
    const set = new Set(availabilityData.occupied);
    if (firstOccupiedNight) set.delete(firstOccupiedNight);
    return set;
  }, [selectingStart, availabilityData, firstOccupiedNight]);

  const calMinDate = useMemo(
    () => (selectingStart ? minDate : parseISODate(checkIn) ?? minDate),
    [selectingStart, checkIn, minDate]
  );

  const calMaxDate = useMemo(
    () =>
      selectingStart ? maxD : firstOccupiedNight ? parseISODate(firstOccupiedNight) : maxD,
    [selectingStart, firstOccupiedNight, maxD]
  );

  // Turnover info notes (half-days at check-in / check-out).
  const isArrivalTurnover = useMemo(
    () => !!checkIn && availabilityData.checkOuts.has(checkIn),
    [checkIn, availabilityData]
  );
  const isDepartureTurnover = useMemo(
    () => !!checkOut && !!firstOccupiedNight && checkOut === firstOccupiedNight,
    [checkOut, firstOccupiedNight]
  );

  const handleSelect = (iso: string) => {
    if (selectingStart) {
      setCheckIn(iso);
      setCheckOut("");
      const d = parseISODate(iso);
      if (d) setView({ year: d.getFullYear(), month: d.getMonth() });
      return;
    }
    if (iso <= checkIn) {
      setCheckIn(iso);
      setCheckOut("");
      return;
    }
    setCheckOut(iso);
    setHover(null);
  };

  const handleVillaChange = (slug: string) => {
    setVillaSlug(slug);
    setCheckIn("");
    setCheckOut("");
    setHover(null);
  };

  const prevMonth = () =>
    setView((v) => {
      const d = new Date(v.year, v.month - 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  const nextMonth = () =>
    setView((v) => {
      const d = new Date(v.year, v.month + 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const a = parseISODate(checkIn);
    const b = parseISODate(checkOut);
    if (!a || !b) return 0;
    return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
  }, [checkIn, checkOut]);

  const format = (iso: string) => {
    const d = parseISODate(iso);
    if (!d) return "";
    return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const villaName = (slug: string) => {
    const v = VILLAS.find((x) => x.slug === slug);
    if (!v) return "";
    return isDe ? v.nameDe : v.nameEn;
  };

  const handleContinue = () => {
    const params = new URLSearchParams();
    params.set("villa", villaSlug);
    if (checkIn) params.set("checkin", checkIn);
    if (checkOut) params.set("checkout", checkOut);
    navigate(localizePath(`/villa-reservation?${params.toString()}`, lang));
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/55"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      <div
        role="dialog"
        aria-modal="true"
        aria-label={isDe ? "Verfügbarkeit prüfen" : "Check availability"}
        className="relative bg-background-50 rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 md:p-6 border-b border-background-200">
          <div className="flex items-start gap-3">
            <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
              <i className="ri-calendar-check-line text-xl"></i>
            </span>
            <div>
              <h3 className="font-heading text-lg md:text-xl text-foreground-950">
                {isDe ? "Verfügbarkeit prüfen" : "Check availability"}
              </h3>
              <p className="text-sm text-foreground-600 mt-0.5">
                {isDe
                  ? "Wählen Sie Ihre Villa und den Zeitraum – wir zeigen Ihnen freie und belegte Tage."
                  : "Choose your villa and dates – we show you free and booked days."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-md bg-red-600 hover:bg-red-700 text-white cursor-pointer shrink-0 transition-colors"
            aria-label={isDe ? "Schließen" : "Close"}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-5 md:p-6">
          {/* Villa tabs */}
          <div className="flex flex-wrap gap-2 mb-5">
            {VILLAS.map((v) => (
              <button
                key={v.slug}
                type="button"
                onClick={() => handleVillaChange(v.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  villaSlug === v.slug
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {isDe ? v.nameDe : v.nameEn}
              </button>
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-5 mb-4 text-xs text-foreground-700">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-background-50 border border-background-300"></span>
              {isDe ? "frei" : "free"}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-red-100/70 border border-red-200"></span>
              {isDe ? "belegt" : "booked"}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded border border-accent-700 relative">
                <span className="absolute bottom-0 right-0 w-1 h-1 rounded-full bg-accent-500"></span>
              </span>
              {isDe ? "Wechseltag (halber Tag)" : "Turnover day (half day)"}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded bg-primary-600"></span>
              {isDe ? "gewählt" : "selected"}
            </span>
          </div>

          <CheckInOutNote isDe={isDe} className="mb-4" />

          {/* Calendar / states */}
          {loading ? (
            <div className="py-16 text-center text-foreground-600">
              <i className="ri-loader-4-line animate-spin text-2xl inline-block mb-3"></i>
              <p>{isDe ? "Verfügbarkeit wird geladen…" : "Loading availability…"}</p>
            </div>
          ) : error ? (
            <div className="py-12 text-center">
              <p className="text-foreground-700 mb-4">
                {isDe
                  ? "Der Kalender konnte nicht geladen werden."
                  : "The calendar could not be loaded."}
              </p>
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-background-50 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                {isDe ? "Erneut versuchen" : "Try again"}
              </button>
            </div>
          ) : (
            <AvailabilityCalendar
              year={view.year}
              month={view.month}
              unavailable={calUnavailable}
              turnover={calTurnover}
              turnoverTooltip={
                isDe
                  ? "Wechseltag — siehe Hinweis unten."
                  : "Turnover day — see note below."
              }
              checkIn={checkIn}
              checkOut={checkOut}
              hover={hover}
              minDate={calMinDate}
              maxDate={calMaxDate}
              isDe={isDe}
              onSelect={handleSelect}
              onHover={setHover}
              onPrev={prevMonth}
              onNext={nextMonth}
            />
          )}

          {isArrivalTurnover && (
            <p className="mt-4 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
              <i className="ri-information-line mt-0.5"></i>
              <span>
                {isDe
                  ? "Am Anreisetag reist ein anderer Gast ab — Anreise ab 15:00 Uhr möglich."
                  : "On the arrival day, another guest departs — check-in possible from 3:00 PM."}
              </span>
            </p>
          )}
          {isDepartureTurnover && (
            <p className="mt-4 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
              <i className="ri-information-line mt-0.5"></i>
              <span>
                {isDe
                  ? "Am Abreisetag reist ein neuer Gast an — bitte bis 12:00 Uhr auschecken."
                  : "On the departure day, another guest arrives — please check out by 12:00 PM."}
              </span>
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-background-200 p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm">
              <div className="text-foreground-600">
                {villaName(villaSlug)}
              </div>
              {checkIn && checkOut ? (
                <div className="text-foreground-950 font-medium mt-0.5">
                  {format(checkIn)} – {format(checkOut)}{" "}
                  <span className="text-foreground-500 font-normal">
                    · {nights} {isDe ? (nights === 1 ? "Nacht" : "Nächte") : nights === 1 ? "night" : "nights"}
                  </span>
                </div>
              ) : (
                <div className="text-foreground-500 mt-0.5">
                  {isDe ? "Bitte An- und Abreise wählen." : "Please select check-in and check-out."}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!checkIn || !checkOut}
              className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-8 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              {isDe ? "Weiter zur Buchung" : "Continue to booking"}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
          <p className="text-xs text-foreground-500 mt-3">
            {isDe
              ? "Die Auswahl wird im Buchungsformular automatisch übernommen."
              : "Your selection is carried over to the booking form automatically."}
          </p>
        </div>
      </div>
    </div>
  );
}